import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Grid, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDateLocale } from "../../../hooks/useDateLocale";
import { Controller, Resolver, SubmitHandler, useForm } from "react-hook-form";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";

import { AuthContext } from "../../../contexts/AuthContext";
import { CONFIG } from "../../../services/config";
import downloadIcon from "../../../assets/images/download.png";

import { FormInputText } from "../../../components/form/FormInputText";
import ActionButtons from "../../../components/MyModal/ActionButtons";
import MyModal from "../../../components/MyModal";

type Props = {
  onClose: () => void;
  openSucessAlert?: () => void;
  openErrorAlert?: (message?: string) => void;
  refreshPage?: () => void;
  setActionType?: (type: string) => void;
};

enum ParticipantType {
  EMAIL = "EMAIL",
  BATCH = "BATCH",
  VEHICLE = "VEHICLE",
}

export interface FormValues {
  campaignName: string;
  type: ParticipantType | "";
  email: string;
  startDate: string;
  endDate: string;
  batchFile: File | null;
  year: string;
  modelId: string;
  versionId: string;
}

interface BatchJob {
  jobId: string;
  status: "pending" | "processing" | "done" | "failed";
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_BATCH_EXTENSIONS = ["xls", "xlsx"];

export default function ModalAddCampaign({
  onClose,
  openSucessAlert,
  openErrorAlert,
  refreshPage,
  setActionType,
}: Props) {
  const { t } = useTranslation();
  const { dateFormat, adapterLocale } = useDateLocale();
  const {
    tokenHeaders,
    country: { countryCode },
  } = useContext(AuthContext);
  const batchInputRef = useRef<HTMLInputElement>(null);
  const batchPollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [batchJob, setBatchJob] = useState<BatchJob>({
    jobId: "",
    status: "pending",
  });
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [vehicleVersions, setVehicleVersions] = useState<any[]>([]);

  const currentDate = new Date();
  const today = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth() + 1,
  ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;

  const formData = yup.object().shape({
    campaignName: yup
      .string()
      .required(t("ottoTrialPeriod.add.validation.campaignNameRequired"))
      .test(
        "no-only-spaces",
        t("ottoTrialPeriod.add.validation.campaignNameRequired"),
        (value) => !value || value.trim().length > 0,
      ),
    type: yup
      .mixed<ParticipantType>()
      .oneOf(Object.values(ParticipantType))
      .required(),
    email: yup.string().when("type", {
      is: ParticipantType.EMAIL,
      then: (schema) =>
        schema
          .required(t("ottoTrialPeriod.add.validation.emailRequired"))
          .email(t("ottoTrialPeriod.add.validation.emailRequired")),
      otherwise: (schema) => schema.notRequired(),
    }),
    batchFile: yup
      .mixed<File>()
      .nullable()
      .when("type", {
        is: ParticipantType.BATCH,
        then: (schema) =>
          schema
            .required(t("ottoTrialPeriod.add.validation.batchFileRequired"))
            .test(
              "batch-file-extension",
              t("ottoTrialPeriod.add.validation.batchFileInvalidExtension"),
              (file) => {
                if (!file) {
                  return false;
                }

                const extension = file.name.split(".").pop()?.toLowerCase();
                return (
                  !!extension && ALLOWED_BATCH_EXTENSIONS.includes(extension)
                );
              },
            )
            .test(
              "batch-file-size",
              t("ottoTrialPeriod.add.validation.batchFileMaxSize"),
              (file) => !file || file.size <= MAX_FILE_SIZE,
            ),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
    startDate: yup.string().when("type", {
      is: (value: any) => !!value,
      then: (schema) =>
        schema.required(t("ottoTrialPeriod.add.validation.startDateRequired")),
      otherwise: (schema) => schema.notRequired(),
    }),
    endDate: yup
      .string()
      .when("type", {
        is: (value: any) => !!value,
        then: (schema) =>
          schema.required(t("ottoTrialPeriod.add.validation.endDateRequired")),
        otherwise: (schema) => schema.notRequired(),
      })
      .test(
        "is-valid-range",
        t("ottoTrialPeriod.add.validation.endDateGreaterThanStartDate"),
        (value, context) => {
          const { type, startDate } = context.parent as FormValues;
          if (!type || !startDate || !value) {
            return true;
          }

          return value > startDate;
        },
      ),
    year: yup.string().when("type", {
      is: ParticipantType.VEHICLE,
      then: (schema) =>
        schema.required(t("ottoTrialPeriod.add.validation.yearRequired")),
      otherwise: (schema) => schema.notRequired(),
    }),
    modelId: yup.string().when("type", {
      is: ParticipantType.VEHICLE,
      then: (schema) =>
        schema.required(t("ottoTrialPeriod.add.validation.modelRequired")),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const { register, handleSubmit, formState, watch, setValue, control } =
    useForm<FormValues>({
      resolver: yupResolver(formData) as unknown as Resolver<FormValues>,
      mode: "onChange",
      reValidateMode: "onChange",
      defaultValues: {
        campaignName: "",
        type: "",
        email: "",
        startDate: "",
        endDate: "",
        batchFile: null,
        year: "",
        modelId: "",
        versionId: "",
      },
    });

  const { errors } = formState;
  const selectedParticipantType = watch("type");
  const selectedBatchFile = watch("batchFile");
  const selectedYear = watch("year");
  const selectedModelId = watch("modelId");
  const selectedVersionId = watch("versionId");
  const availableVehicleYears = Array.from(
    new Set(vehicleVersions.map((version: any) => version.year)),
  );
  const availableVehicleVersions = selectedYear
    ? vehicleVersions.filter(
        (version: any) => String(version.year) === selectedYear,
      )
    : vehicleVersions;
  const hasSelectedParticipantType = Boolean(selectedParticipantType);
  const showEmailFields = selectedParticipantType === ParticipantType.EMAIL;
  const showBatchFields = selectedParticipantType === ParticipantType.BATCH;
  const showVehicleFields = selectedParticipantType === ParticipantType.VEHICLE;

  const handleBatchFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setValue("batchFile", file, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const openBatchFileChooser = () => {
    batchInputRef.current?.click();
  };

  const getVehicles = async () => {
    try {
      const response = await axios.get(
        `${CONFIG.API_URL}vehicles?countryCode=${countryCode}&`,
        tokenHeaders,
      );
      setVehicles(response.data);
    } catch (error) {
      setVehicles([]);
    }
  };

  const getVehicleVersions = async (modelId: string) => {
    try {
      if (modelId) {
        const response = await axios.get(
          `${CONFIG.API_URL}vehicles/${modelId}`,
          tokenHeaders,
        );
        setVehicleVersions(response.data.versions || []);
      } else {
        setVehicleVersions([]);
      }
    } catch (error) {
      setVehicleVersions([]);
    }
  };

  useEffect(() => {
    if (showVehicleFields) {
      getVehicles();
    }
  }, [showVehicleFields]);

  useEffect(() => {
    if (selectedModelId) {
      getVehicleVersions(selectedModelId);
      setValue("year", "");
      setValue("versionId", "");
    }
  }, [selectedModelId, setValue]);

  useEffect(() => {
    if (selectedYear) {
      setValue("versionId", "");
    }
  }, [selectedYear, setValue]);

  const verifyBatchStatus = async (jobId: string) => {
    const response = await axios.get(
      `${CONFIG.API_URL}temporaryAccess/batch/${jobId}`,
      tokenHeaders,
    );
    return response.data;
  };

  const startBatchStatusLoopCheck = async (jobId: string) => {
    const result = await verifyBatchStatus(jobId);

    if (result.status === "PENDING" || result.status === "PROCESSING") {
      batchPollingTimeoutRef.current = setTimeout(() => {
        setBatchJob({ jobId, status: "processing" });
      }, 3000);

      return;
    }

    if (result.status === "DONE") {
      setBatchJob({ jobId, status: "done" });
      return;
    }

    setBatchJob({ jobId, status: "failed" });
  };

  useEffect(() => {
    if (batchJob.status === "processing") {
      startBatchStatusLoopCheck(batchJob.jobId).catch(() => {
        setBatchJob({ jobId: batchJob.jobId, status: "failed" });
      });

      return;
    }

    if (batchJob.status === "done") {
      refreshPage?.();
      onClose();
      openSucessAlert?.();
      setActionType?.("add");
      return;
    }

    if (batchJob.status === "failed") {
      refreshPage?.();
      onClose();
      openErrorAlert?.();
    }
  }, [
    batchJob,
    onClose,
    openErrorAlert,
    openSucessAlert,
    refreshPage,
    setActionType,
  ]);

  useEffect(() => {
    return () => {
      if (batchPollingTimeoutRef.current) {
        clearTimeout(batchPollingTimeoutRef.current);
      }
    };
  }, []);

  const HorizontalLine = (
    <Box
      sx={{
        borderTop: "1px solid var(--gray-100)",
        marginBottom: 3,
        marginTop: 2,
      }}
    />
  );

  const addCampaignService: SubmitHandler<FormValues> = async (values) => {
    const basePayload = {
      type: values.type,
      campaignName: values.campaignName,
      countryCode: tokenHeaders.headers.countryCode,
      startDate: `${values.startDate}T00:00:00`,
      endDate: `${values.endDate}T00:00:00`,
    };

    try {
      switch (values.type) {
        case ParticipantType.BATCH: {
          if (!values.batchFile) {
            throw new Error(
              t("ottoTrialPeriod.add.validation.batchFileRequired"),
            );
          }

          const formDataBatch = new FormData();
          formDataBatch.append("file", values.batchFile);
          formDataBatch.append(
            "metadata",
            JSON.stringify({
              campaignName: values.campaignName,
              countryCode: tokenHeaders.headers.countryCode,
              startDate: `${values.startDate}T00:00:00`,
              endDate: `${values.endDate}T00:00:00`,
            }),
          );

          const batchResponse = await axios.post(
            `${CONFIG.API_URL}temporaryAccess/batch`,
            formDataBatch,
            {
              ...tokenHeaders,
              headers: {
                ...tokenHeaders.headers,
                "Content-Type": "multipart/form-data",
              },
            },
          );

          const { jobId } = batchResponse.data;
          setBatchJob({ jobId, status: "processing" });
          break;
        }

        case ParticipantType.EMAIL: {
          await axios.post(
            `${CONFIG.API_URL}temporaryAccess/campaign`,
            {
              ...basePayload,
              email: values.email,
            },
            tokenHeaders,
          );
          break;
        }

        case ParticipantType.VEHICLE: {
          const vehiclePayload = {
            ...basePayload,
            modelId: values.modelId,
            vehicleYear: values.year,
            versionId: values.versionId,
          };

          await axios.post(
            `${CONFIG.API_URL}temporaryAccess/campaign`,
            vehiclePayload,
            tokenHeaders,
          );
          break;
        }

        default:
          throw new Error("Unsupported participant type");
      }

      if (values.type === ParticipantType.BATCH) {
        return;
      }

      refreshPage?.();
      onClose();
      openSucessAlert?.();
      setActionType?.("add");
    } catch (error: any) {
      refreshPage?.();
      onClose();
      if (error?.response?.status === 409) {
        return openErrorAlert?.(
          t("ottoTrialPeriod.alert.campaignAlreadyExists"),
        );
      }
      return openErrorAlert?.();
    }
  };

  return (
    <MyModal
      title={t("ottoTrialPeriod.add.title")}
      modalChange={onClose}
      component="form"
      onSubmit={handleSubmit(addCampaignService)}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <span>{t("ottoTrialPeriod.add.identification")}</span>
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormInputText
                id="campaignName"
                type="text"
                label={t("ottoTrialPeriod.add.campaignName")}
                slotProps={{ htmlInput: { maxLength: 50, minLength: 1 } }}
                error={errors.campaignName}
                {...register("campaignName")}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormInputText
                select
                id="type"
                label={t("ottoTrialPeriod.add.participantType")}
                error={errors.type}
                value={selectedParticipantType || ""}
                {...register("type")}
              >
                {Object.values(ParticipantType).map((type) => (
                  <MenuItem key={type} value={type}>
                    {t(
                      `ottoTrialPeriod.add.participantTypeOptions.${type.toLowerCase()}`,
                    )}
                  </MenuItem>
                ))}
              </FormInputText>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {HorizontalLine}

      {showEmailFields && (
        <>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <span>{t("ottoTrialPeriod.add.participants")}</span>
            </Grid>
            <Grid size={{ xs: 12, md: 9 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormInputText
                    id="emal"
                    type="text"
                    label={t("ottoTrialPeriod.add.email")}
                    error={errors.email}
                    {...register("email")}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {HorizontalLine}
        </>
      )}

      {showBatchFields && (
        <>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <span>{t("ottoTrialPeriod.add.batch.title")}</span>
            </Grid>
            <Grid size={{ xs: 12, md: 9 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <input
                    ref={batchInputRef}
                    type="file"
                    accept=".xls,.xlsx"
                    style={{ display: "none" }}
                    onChange={handleBatchFileChange}
                  />
                  <Button
                    variant="contained"
                    type="button"
                    onClick={openBatchFileChooser}
                  >
                    {t("ottoTrialPeriod.add.batch.selectFileButton")}
                  </Button>
                  <p
                    style={{
                      margin: 0,
                      marginBottom: 10,
                      fontSize: 12,
                      color: "var(--black-200)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                    }}
                  >
                    {selectedBatchFile?.name || ""}
                  </p>
                </div>
                <a
                  style={{
                    display: "flex",
                    marginBottom: 10,
                    fontSize: 12,
                    alignItems: "center",
                    flexDirection: "row-reverse",
                    gap: 6,
                    textDecoration: "underline",
                    color: "var(--blue-200)",
                  }}
                  href={CONFIG.PUSH_SAMPLE_DOC || "#"}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span>{t("ottoTrialPeriod.add.batch.downloadTemplate")}</span>
                  <img
                    src={downloadIcon}
                    alt=""
                    aria-hidden="true"
                    style={{ width: 14, height: 14, flexShrink: 0 }}
                  />
                </a>
              </div>
              {!!errors.batchFile && (
                <p
                  style={{
                    marginTop: 8,
                    marginBottom: 0,
                    fontSize: 12,
                    color: "var(--red-100)",
                  }}
                >
                  {errors.batchFile.message}
                </p>
              )}
            </Grid>
          </Grid>

          <Grid container spacing={0} sx={{ mt: { xs: 0, md: "-14px" } }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <p style={{ marginTop: 0, marginBottom: 0, fontSize: 12 }}>
                {t("ottoTrialPeriod.add.batch.acceptedType")}
              </p>
              <p style={{ marginTop: 2, marginBottom: 0, fontSize: 12 }}>
                {t("ottoTrialPeriod.add.batch.maxSize")}
              </p>
            </Grid>
          </Grid>

          {HorizontalLine}
        </>
      )}

      {showVehicleFields && (
        <>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <span>{t("ottoTrialPeriod.add.vehicle")}</span>
            </Grid>
            <Grid size={{ xs: 12, md: 9 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormInputText
                    select
                    id="modelId"
                    label={t("ottoTrialPeriod.add.model")}
                    error={errors.modelId}
                    value={selectedModelId || ""}
                    {...register("modelId")}
                  >
                    {vehicles.map((vehicle) => (
                      <MenuItem key={vehicle.id} value={String(vehicle.id)}>
                        {vehicle.name}
                      </MenuItem>
                    ))}
                  </FormInputText>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormInputText
                    select
                    id="year"
                    label={t("ottoTrialPeriod.add.year")}
                    error={errors.year}
                    disabled={!selectedModelId}
                    value={selectedYear || ""}
                    {...register("year")}
                  >
                    {availableVehicleYears.map((year: any) => (
                      <MenuItem key={`year-${year}`} value={String(year)}>
                        {year}
                      </MenuItem>
                    ))}
                  </FormInputText>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormInputText
                    select
                    id="versionId"
                    label={t("ottoTrialPeriod.add.version")}
                    error={errors.versionId}
                    disabled={!selectedModelId || !selectedYear}
                    value={selectedVersionId || ""}
                    {...register("versionId")}
                  >
                    {availableVehicleVersions.map((version: any) => (
                      <MenuItem key={version.id} value={String(version.id)}>
                        {version.name}
                      </MenuItem>
                    ))}
                  </FormInputText>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {HorizontalLine}
        </>
      )}

      {hasSelectedParticipantType && (
        <>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <span>{t("ottoTrialPeriod.add.period")}</span>
              </Grid>
              <Grid size={{ xs: 12, md: 9 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="startDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          label={t("ottoTrialPeriod.add.startDate")}
                          format={dateFormat}
                          minDate={dayjs(today)}
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(val) =>
                            field.onChange(
                              val ? dayjs(val).format("YYYY-MM-DD") : "",
                            )
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              error: !!errors.startDate,
                              helperText: errors.startDate?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="endDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          label={t("ottoTrialPeriod.add.endDate")}
                          format={dateFormat}
                          minDate={dayjs(today)}
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(val) =>
                            field.onChange(
                              val ? dayjs(val).format("YYYY-MM-DD") : "",
                            )
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              error: !!errors.endDate,
                              helperText: errors.endDate?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </LocalizationProvider>

          {HorizontalLine}
        </>
      )}

      <p style={{ fontSize: 12, marginBottom: 30 }}>
        {t("ottoTrialPeriod.add.requiredFields")}
      </p>

      {batchJob.status === "processing" || formState.isSubmitting ? (
        <CircularProgress />
      ) : (
        <ActionButtons
          typeButton="submit"
          saveName={t("ottoTrialPeriod.add.saveButton")}
          disableSave={!formState.isValid}
          enableLine={false}
        />
      )}
    </MyModal>
  );
}
